import type { Assessment } from "../assessment-types";

/** 🌿 Nervous System & Survival Responses — phase 1 expansion. */

export const TRIGGER_PATTERNS_ASSESSMENT: Assessment = {
  slug: "trigger-patterns",
  emoji: "🎯",
  title: "Trigger Patterns",
  tagline: "What tends to activate your nervous system?",
  category: "nervous-system",
  keywords: ["trigger", "triggered", "activation", "reminder", "flashback", "reaction"],
  intro:
    "This reflects on the situations, sounds, tones or moments that seem to switch something on inside you. Noticing your own patterns is information, not a fault.",
  questions: [
    { id: "q1", text: "Do certain tones of voice change how you feel almost instantly?" },
    { id: "q2", text: "Do you notice strong reactions that feel bigger than the situation itself?" },
    { id: "q3", text: "Do particular places, smells or sounds bring back difficult feelings?" },
    { id: "q4", text: "Do you feel your body react before you have understood why?" },
    { id: "q5", text: "Do you find it hard to settle again once something has activated you?" },
    { id: "q6", text: "Do you plan your day around avoiding certain situations or people?" },
    { id: "q7", text: "Do arguments or raised voices affect you long after they end?" },
    { id: "q8", text: "Do you feel confused afterwards about what set the reaction off?" },
    { id: "q9", text: "Do reminders of the past appear when you are not expecting them?" },
    { id: "q10", text: "Do you notice tension, heat or a sinking feeling in your body at these moments?" },
    { id: "q11", text: "Do you find it hard to explain your reactions to other people?" },
    { id: "q12", text: "Do you wish you understood your own reactions more clearly?" },
  ],
  bands: [
    {
      key: "low",
      minPercent: 0,
      headline: "Your answers suggest triggers rarely take over for you at the moment.",
      body:
        "Most people have some reminders that stir something up. Knowing your own early signals can help you stay steady when one appears.",
    },
    {
      key: "some",
      minPercent: 30,
      headline: "Your responses suggest you notice some triggering moments.",
      body:
        "Triggers form when your system links a present cue to a past experience. Mapping which cues matter to you often makes them feel less random.",
    },
    {
      key: "often",
      minPercent: 55,
      headline: "Your responses suggest triggers may be affecting you fairly often.",
      body:
        "This can look like sudden intensity, withdrawal or confusion afterwards. Naming the pattern and having a settling practice ready can make a real difference.",
    },
    {
      key: "high",
      minPercent: 78,
      headline: "Your responses suggest activation may be showing up strongly and often.",
      body:
        "Living with frequent activation is tiring, and it makes sense as a protective adaptation. Going gently, in small steps and with support you trust, tends to help more than pushing through.",
    },
  ],
  explore: {
    understand: [
      { label: "Understanding triggers", to: "/learn/triggers" },
      { label: "The window of tolerance", to: "/learn/window-of-tolerance" },
    ],
    try: [
      { label: "Grounding exercises", to: "/grounding" },
      { label: "Quick nervous-system resets", to: "/quick-resets" },
    ],
    reflect: [
      { label: "Trigger Journal", to: "/trigger-journal" },
      { label: "Triggers as teachers", to: "/integrate/triggers-as-teachers" },
    ],
  },
};

export const FUNCTIONAL_FREEZE_ASSESSMENT: Assessment = {
  slug: "functional-freeze",
  emoji: "🧊",
  title: "Functional Freeze",
  tagline: "When you're functioning but feel stuck inside",
  category: "nervous-system",
  keywords: ["freeze", "stuck", "shutdown", "autopilot", "immobility", "flat", "going through the motions"],
  intro:
    "This reflects on what it's like to keep going on the outside while feeling stalled, distant or heavy on the inside. There are no right answers here.",
  questions: [
    { id: "q1", text: "Do you get through the day while feeling somewhat absent from it?" },
    { id: "q2", text: "Do you find it hard to start tasks even when you want to do them?" },
    { id: "q3", text: "Do you feel physically heavy, slow or hard to move?" },
    { id: "q4", text: "Do you look fine to others while feeling stuck inside?" },
    { id: "q5", text: "Do you spend time staring or scrolling without really choosing to?" },
    { id: "q6", text: "Do decisions, even small ones, feel harder than they should?" },
    { id: "q7", text: "Do you feel disconnected from what you used to enjoy?" },
    { id: "q8", text: "Do you notice long stretches where feelings seem muted?" },
    { id: "q9", text: "Do you push through the day and then collapse afterwards?" },
    { id: "q10", text: "Do you find it hard to rest even though you feel exhausted?" },
    { id: "q11", text: "Do you criticise yourself for not doing more?" },
    { id: "q12", text: "Do you sense there is something underneath the stuckness you haven't had space for?" },
  ],
  bands: [
    {
      key: "low",
      minPercent: 0,
      headline: "Your answers suggest this stuck, flattened state is not a strong feature right now.",
      body:
        "Everyone has slower days. Knowing what your own version of freeze feels like helps you catch it early if it returns.",
    },
    {
      key: "some",
      minPercent: 30,
      headline: "Your responses suggest you may recognise moments of functional freeze.",
      body:
        "Freeze is a protective state your body can use when action feels unsafe or overwhelming. Very small movement, warmth and orienting to the room often help it soften.",
    },
    {
      key: "often",
      minPercent: 55,
      headline: "Your responses suggest a stuck or shut-down state may be present quite often.",
      body:
        "This is not laziness. It is a nervous-system state that developed for a reason. Gentle, tiny actions tend to work better than trying to force yourself back into gear.",
    },
    {
      key: "high",
      minPercent: 78,
      headline: "Your responses suggest you may be spending a lot of time in a functional freeze state.",
      body:
        "Keeping going while feeling this stuck takes an enormous amount from you. Support from someone appropriately qualified alongside gentle daily regulation can be worth considering.",
    },
  ],
  explore: {
    understand: [
      { label: "Trauma responses explained", to: "/learn/trauma-responses" },
      { label: "The polyvagal ladder", to: "/learn/polyvagal-ladder" },
    ],
    try: [
      { label: "Somatic practices", to: "/somatic" },
      { label: "Nervous-system reset", to: "/nervous-system-reset" },
    ],
    reflect: [
      { label: "Guided journal", to: "/guided-journal" },
      { label: "Daily check-in", to: "/check-in" },
    ],
  },
};

export const SURVIVAL_MODE_ASSESSMENT: Assessment = {
  slug: "survival-mode",
  emoji: "🧭",
  title: "Survival Mode",
  tagline: "Are you still living as though you have to survive?",
  category: "nervous-system",
  keywords: ["survival mode", "coping", "burnout", "surviving", "exhausted", "always on"],
  intro:
    "This reflects on whether daily life still feels like something to get through rather than something to live. It is a pattern many people recognise long after the hardest times have passed.",
  questions: [
    { id: "q1", text: "Do you often feel like you're getting through the day rather than living it?" },
    { id: "q2", text: "Do you find it hard to relax even when nothing is wrong?" },
    { id: "q3", text: "Do you plan ahead for things going wrong?" },
    { id: "q4", text: "Do you struggle to imagine what you actually want, rather than what you have to do?" },
    { id: "q5", text: "Do you keep going until your body makes you stop?" },
    { id: "q6", text: "Do calm periods feel unfamiliar or slightly uneasy?" },
    { id: "q7", text: "Do you put your own needs last without noticing?" },
    { id: "q8", text: "Do you feel responsible for holding everything together?" },
    { id: "q9", text: "Do you find it hard to ask for help?" },
    { id: "q10", text: "Do you feel tired in a way that sleep doesn't fix?" },
    { id: "q11", text: "Do you notice you rarely stop to enjoy anything?" },
    { id: "q12", text: "Do you sense there is a version of life beyond coping that you haven't reached yet?" },
  ],
  bands: [
    {
      key: "low",
      minPercent: 0,
      headline: "Your answers suggest you have some room beyond simply coping right now.",
      body:
        "That space is worth protecting. Noticing what helps you stay there is as useful as noticing what pulls you out of it.",
    },
    {
      key: "some",
      minPercent: 30,
      headline: "Your responses suggest parts of your life may still run on survival mode.",
      body:
        "Survival mode develops when your system has had to stay prepared. Small pockets of rest and enjoyment are often the first way out of it.",
    },
    {
      key: "often",
      minPercent: 55,
      headline: "Your responses suggest survival mode may be a familiar way of living for you.",
      body:
        "This usually reflects how much you have had to carry, not a lack of effort. Moving beyond survival mode is generally slow, and that is normal.",
    },
    {
      key: "high",
      minPercent: 78,
      headline: "Your responses suggest most of your energy may be going into simply getting through.",
      body:
        "That is exhausting and completely understandable. Recovery often begins with one small thing that is for you rather than for coping — and with support you trust.",
    },
  ],
  explore: {
    understand: [
      { label: "Complex trauma explained", to: "/learn/cptsd" },
      { label: "The window of tolerance", to: "/learn/window-of-tolerance" },
    ],
    try: [
      { label: "Acts of self-care", to: "/acts-of-self-care" },
      { label: "Relaxation practices", to: "/relaxation-practices" },
    ],
    reflect: [
      { label: "Life worth living", to: "/integrate/life-worth-living" },
      { label: "My healing journey", to: "/my-healing-journey" },
    ],
  },
};
